// MACHINERY V2 (Stroje) — SR detail: the footer action row (M2.6).
//
// Spec: data package §4 "Actions (+ gates)" + ticket §10. Reference impl:
// design_handoff_machinery_v3/machinery-sr.jsx `SrFooterActions` (lines
// 492-513) and v1 srDetail/SrActionBar.tsx. Artboards 0700 / 0701 draw
// "← Späť na stroj" on the left and the buttons right-aligned.
//
// >>> THE ACTION SET IS THE SR LIST'S. THE GATES ARE THE SR LIST'S. <<<
// The page hands us whatever `buildSrActions` (profile/tabs/srTabActions.ts,
// M2.5) produced for this report and this user — the same builder the tab kebab
// feeds. Two v2 surfaces, one gate implementation, so they cannot disagree about
// what a Worker may do. This mirrors the M2.4 precedent where ProfileHero
// consumes `buildFleetActions` rather than re-gating.
//
// This component therefore contains NO permission logic at all: it maps ids to a
// glyph and to the detail page's own wording, and renders what it is given.
//
// LABEL OVERRIDES, NOT NEW STRINGS. The kebab is terse ("Upraviť"); the artboard
// footer is explicit ("Upraviť správu"). Three ids take the `srDetail.*` copy v1
// already ships for this surface; every other id keeps the builder's label.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { FleetMenuAction } from "../fleet/FleetCardMenu";
import {
  CheckGlyph,
  EditGlyph,
  PdfGlyph,
  ShareGlyph,
  WrenchGlyph,
} from "../forms/FormGlyphs";

/** id -> the detail page's own label key (v1 srDetail/SrActionBar.tsx:84 / :92 / :101). */
const LABEL_OVERRIDE: Record<string, string> = {
  share: "myMachinery:srDetail.shareReport",
  pdf: "myMachinery:srDetail.downloadPdf",
  edit: "myMachinery:srDetail.editReport",
};

const GLYPH: Record<string, React.ReactNode> = {
  share: <ShareGlyph />,
  pdf: <PdfGlyph />,
  rfs: <WrenchGlyph />,
  edit: <EditGlyph />,
  solve: <CheckGlyph />,
  editSolved: <EditGlyph />,
};

export interface SrDetailActionsProps {
  actions: FleetMenuAction[];
  onBack: () => void;
}

const SrDetailActions: React.FC<SrDetailActionsProps> = ({ actions, onBack }) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
        padding: "12px 0 0",
        borderTop: "1px solid var(--border-soft)",
        minWidth: 0,
      }}
    >
      <button
        type="button"
        onClick={onBack}
        style={{
          border: 0,
          background: "transparent",
          padding: 0,
          font: "inherit",
          fontSize: 13,
          fontWeight: 500,
          color: "var(--mod-accent)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        ← {translate("myMachinery:srDetail.backToMachine")}
      </button>
      <span style={{ flex: 1 }} />
      {actions.map((a) => (
        <button
          key={a.id}
          type="button"
          // `solve` is the one call to action on an open report — the artboard
          // gives the last button the module accent.
          className={a.id === "solve" ? "btn btn-mod" : "btn"}
          onClick={a.onSelect}
          disabled={a.disabled}
        >
          {GLYPH[a.id]}
          {translate(LABEL_OVERRIDE[a.id] || a.labelKey)}
        </button>
      ))}
    </div>
  );
};

export default SrDetailActions;
